// === GENERATED:SCREEN:CONFIRMATION ===
// Application flow - Confirmation & Success
// Customer: SAA

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useConfig,
  WorkflowPageLayout,
  getCurrentApplicationStepNumber,
  calculateApplicationSteps,
  formatPrice,
  createRequest,
  isDebugMode,
  computeAvpDisplayPremium,
  computeWoonhuisDisplayPremium,
} from '@sluitstraat/core';
import { getApplicationName } from '../../utils/appName';
import { PremiumBreakdownCard } from '../../components/PremiumBreakdownCard';
import { USE_BREAKDOWN_UI } from '../../config/uiFlags';
import { DEFAULT_PRIVACY_URL } from '../../config/privacy';
import { getProductContent } from '../../products/content';
import { computeInboedelTotalFromSections } from '../../utils/inboedelPricing';
import { computePolicyCostAllocation } from '../../utils/policyCost';
import { inboedelGlassAllowed } from '../../utils/glassPremium';
import { keepSelectedEcRow } from './inboedel/coverages';
import { orderSaaCoveragesInDocument } from '../../utils/autoSectionOrder';
import { applyCaDerivedFields } from '../../utils/autoGarantie';
import { applyPpDefaults } from '../../utils/ppDefaults';
import { RBV_PP_REQUEST_DEFAULTS } from './rechtsbijstand/fields';
import { WOONHUIS_PP_REQUEST_DEFAULTS } from './woonhuis/fields';
import { DOORLOPENDE_REIS_CARRIER, DOORLOPENDE_REIS_MYAAND, DOORLOPENDE_REIS_PP_REQUEST_DEFAULTS } from './doorlopendereis/fields';
import { restoreDoorlopendeReisInputs } from './doorlopendereis/calc';
import { ONGEVALLEN_CARRIER, ONGEVALLEN_MYAAND, ONGEVALLEN_DEFAULTS, ONGEVALLEN_PP_REQUEST_DEFAULTS } from './ongevallen/fields';
import { normalizeOngevallenVwCodes, restoreOngevallenInputs, restoreOngevallenInsured, sortAfdDocument } from './ongevallen/calc';
import { AUTO_DOWNSTREAM_OB_ALLOWLIST, LI_KENTEKENHOUDER_PERSONAL, afmkmCode, afmkmWireKm } from './personenauto/fields';
import { RBV_IN_EIGBVAK_DEFAULT } from './rechtsbijstand/fields';
import { WorkflowDebugPanel } from '../../components/WorkflowDebugPanel';
import { HelpCard } from '../../components/HelpCard';
import { SubscriptionCard } from '../../components/SubscriptionCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { buildPremiumDebugSection, readDebugIds, readEnrichedDocument, collectProducts } from '../../utils/debugData';
import { confirmationStyles as styles } from '../../styles/commonStyles';
import { hasAnySelectedProduct } from '../../utils/productGuards';
import { requestSubmittedKey } from './summary/sessionState';
import { setRequestInFlight } from '../../utils/requestState';
import { ApplicantSummary } from '../../components/ApplicantSummary';
import { GSS_CODE_TO_I18N_KEY } from '../../utils/familyComposition';

/**
 * Get application name from config
 * Falls back to default if not available
 */

export const ConfirmationScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data, paymentData, setPaymentData, selectedProducts, getStepData, clearWorkflowData } = useWorkflow();
  const { config } = useConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [termsError, setTermsError] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(paymentData?.paymentInfo?.privacyAccepted || false);
  const [privacyError, setPrivacyError] = useState(false);
  const [sepaAccepted, setSepaAccepted] = useState(paymentData?.paymentInfo?.sepaAccepted || false);
  const [sepaError, setSepaError] = useState(false);
  const [appName, setAppName] = useState<string>('Insurance Application');

  // Load application name from config
  React.useEffect(() => {
    getApplicationName('Insurance Application').then(setAppName);
  }, []);

  // Scroll to top on mount
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const submittedRelationId = sessionStorage.getItem('vp_idnr');
  const submittedQuoteId = sessionStorage.getItem('quote_id');
  const alreadySubmitted = !!(submittedRelationId && submittedQuoteId
    && sessionStorage.getItem(requestSubmittedKey(submittedRelationId, submittedQuoteId)) === 'true');

  React.useEffect(() => {
    if (alreadySubmitted) {
      clearWorkflowData();
      navigate('/workflow/product-selection', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only already-submitted check
  }, []);

  // Validate required data on mount - redirect if missing critical data
  React.useEffect(() => {
    if (alreadySubmitted) return;
    // Check if at least one product is selected (generic — a hardcoded per-product list
    // here silently bounces any product not enumerated; see utils/productGuards.ts).
    if (!hasAnySelectedProduct(selectedProducts)) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = data['client-information']?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }

    // Check if payment information exists (check step data first, then context)
    const paymentStepData = getStepData('payment-information');
    const paymentInfo = paymentStepData?.paymentInfo || paymentData?.paymentInfo;
    if (!paymentInfo?.iban || !paymentInfo?.startDate) {
      navigate('/workflow/payment-information', { replace: true });
      return;
    }

    // Check if acceptance questions have been completed
    const acceptanceData = getStepData('acceptance-questions');
    if (!acceptanceData || !acceptanceData.formData) {
      navigate('/workflow/acceptance-questions', { replace: true });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on data/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // The product-level RI_ALGVRWD is preset to 'J' by the calculation step
  // (rechtsbijstand requires it to price), but that does NOT mean the
  // customer has agreed to the Slotverklaring. The Slotverklaring checkbox
  // is driven solely by `paymentInfo.acceptTerms`, which only flips to true
  // when the customer explicitly ticks the box on this page. If the customer
  // navigates back and returns, their prior tick is preserved through
  // paymentData context.

  // Handle checkbox changes - update both paymentData and acceptance-questions formData
  const handleCheckboxChange = (value: boolean) => {
    // Clear error when checkbox is checked
    if (value) {
      setTermsError(false);
    }

    // Update paymentData for UI state
    setPaymentData({
      ...paymentData,
      paymentInfo: {
        ...paymentData?.paymentInfo,
        acceptTerms: value,
      },
    });

    // Also update the acceptance-questions formData with the terms data
    // The formData now contains products: { avp: {...}, woonhuis: {...}, inboedel: {...} }
    const acceptanceQuestionsData = getStepData('acceptance-questions');
    if (acceptanceQuestionsData?.formData) {
      const formData = acceptanceQuestionsData.formData;

      // Update terms for ALL selected products
      if (formData.avp) {
        if (!formData.avp.Contractdocument) formData.avp.Contractdocument = {};
        if (!formData.avp.Contractdocument.PP) formData.avp.Contractdocument.PP = {};
        if (!formData.avp.Contractdocument.PP.RI) formData.avp.Contractdocument.PP.RI = {};
        formData.avp.Contractdocument.PP.RI.RI_ALGVRWD = value ? 'J' : 'N';
      }

      if (formData.woonhuis) {
        if (!formData.woonhuis.Contractdocument) formData.woonhuis.Contractdocument = {};
        if (!formData.woonhuis.Contractdocument.PP) formData.woonhuis.Contractdocument.PP = {};
        if (!formData.woonhuis.Contractdocument.PP.RI) formData.woonhuis.Contractdocument.PP.RI = {};
        formData.woonhuis.Contractdocument.PP.RI.RI_ALGVRWD = value ? 'J' : 'N';
      }

      if (formData.inboedel) {
        if (!formData.inboedel.Contractdocument) formData.inboedel.Contractdocument = {};
        if (!formData.inboedel.Contractdocument.PP) formData.inboedel.Contractdocument.PP = {};
        if (!formData.inboedel.Contractdocument.PP.RI) formData.inboedel.Contractdocument.PP.RI = {};
        formData.inboedel.Contractdocument.PP.RI.RI_ALGVRWD = value ? 'J' : 'N';
      }

      if (formData.personenauto) {
        if (!formData.personenauto.Contractdocument) formData.personenauto.Contractdocument = {};
        if (!formData.personenauto.Contractdocument.PP) formData.personenauto.Contractdocument.PP = {};
        if (!formData.personenauto.Contractdocument.PP.RI) formData.personenauto.Contractdocument.PP.RI = {};
        formData.personenauto.Contractdocument.PP.RI.RI_ALGVRWD = value ? 'J' : 'N';
      }

      if (formData.rechtsbijstand) {
        if (!formData.rechtsbijstand.Contractdocument) formData.rechtsbijstand.Contractdocument = {};
        if (!formData.rechtsbijstand.Contractdocument.PP) formData.rechtsbijstand.Contractdocument.PP = {};
        if (!formData.rechtsbijstand.Contractdocument.PP.RI) formData.rechtsbijstand.Contractdocument.PP.RI = {};
        formData.rechtsbijstand.Contractdocument.PP.RI.RI_ALGVRWD = value ? 'J' : 'N';
      }

      // Save updated formData back to workflow
      data['acceptance-questions'] = acceptanceQuestionsData;
    }
  };

  const handleConfirm = async () => {

    // One-time poliskosten per product, cap-aware (€12,50 car / €6,25 other, max €12,50 per package).
    const policyAllocation = computePolicyCostAllocation(selectedProducts, config);

    // Validate all declarations are accepted
    const termsNotAccepted = !paymentData?.paymentInfo?.acceptTerms;
    const privacyNotAccepted = !privacyAccepted;
    const sepaNotAccepted = !sepaAccepted;

    if (termsNotAccepted || privacyNotAccepted || sepaNotAccepted) {
      if (termsNotAccepted) setTermsError(true);
      if (privacyNotAccepted) setPrivacyError(true);
      if (sepaNotAccepted) setSepaError(true);
      // Scroll to first unchecked
      const firstErrorId = termsNotAccepted ? 'accept-terms' : privacyNotAccepted ? 'accept-privacy' : 'accept-sepa';
      const el = document.getElementById(firstErrorId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);
    setRequestInFlight(true);
    setSubmitError(null);
    setTermsError(false);

    try {
      // Get IDs from sessionStorage
      const relationId = sessionStorage.getItem('vp_idnr');
      const quoteId = sessionStorage.getItem('quote_id');
      const calculationId = sessionStorage.getItem('calculation_id');

      if (!relationId) {
        throw new Error('Relation ID not found. Please go back and complete the quote process.');
      }

      if (!quoteId) {
        throw new Error('Quote ID not found. Please go back and complete the quote process.');
      }


      // Build VP data from client information
      const clientInfo = data['client-information']?.clientInfo || {};
      const vpData = {
        VP_VOORL: clientInfo.initials,
        VP_ANAAM: clientInfo.lastName,
        VP_VOORV: clientInfo.affix,
        VP_GESLACH: clientInfo.gender,
        VP_EMAIL: clientInfo.email,
        VP_TELNUM: clientInfo.phone,
        VP_PARZAKC: 'P', // P for Particulier (Individual)
        VP_IDNR: relationId,
        // VP_AANHEFC / VP_TITULAT — zero-padded 5-char gender codes ('00001' / '00002'),
        // mirrors the derivation in createRelation (clientService.ts).
        VP_AANHEFC: clientInfo.salutationCode
          || (clientInfo.gender === 'M' ? '00001' : clientInfo.gender === 'V' ? '00002' : undefined),
        VP_TITULAT: clientInfo.titulature
          || (clientInfo.gender === 'M' ? '00001' : clientInfo.gender === 'V' ? '00002' : undefined),
      } as any;

      // Add date of birth if available
      if (clientInfo.dateOfBirth) {
        const parts = clientInfo.dateOfBirth.split('-');
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        vpData.VP_GEBDAT = Number(`${year}${month}${day}`);
      }

      // Add today's date in N8 format (YYYYMMDD) for VP_INGDTW
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      vpData.VP_INGDTW = Number(`${year}${month}${day}`);

      // Add address information from workflow data (any product with BA)
      const woonhuisData = data['woonhuis']?.Contractdocument?.PP?.BA;
      const inboedelData = data['inboedel']?.Contractdocument?.PP?.BA;
      const avpData = data['aansprakelijkheid']?.Contractdocument?.PP?.BA;
      const autoData = data['personenauto']?.Contractdocument?.PP?.BA;
      const rbData = data['rechtsbijstand']?.Contractdocument?.PP?.BA;
      const addressData = woonhuisData || inboedelData || avpData || autoData || rbData;

      if (addressData) {
        if (addressData.BA_STRAAT) {
          vpData.VP_STRAAT = addressData.BA_STRAAT;
        }
        if (addressData.BA_HUISNR) {
          vpData.VP_HUISNR = Number(addressData.BA_HUISNR);
        }
        if (addressData.BA_TOEVOEG) {
          vpData.VP_TOEVOEG = addressData.BA_TOEVOEG;
        }
        if (addressData.BA_PCODE) {
          vpData.VP_PCODE = addressData.BA_PCODE;
        }
        if (addressData.BA_PLAATS) {
          vpData.VP_PLAATS = addressData.BA_PLAATS;
        }

        // Set address status to AD2 when address is present
        vpData.VP_ADPSTAT = 'AD2';
      } else {
        // A VP-address product (DR/Ongevallen) may be the only source of the policyholder address.
        const drVp = data['ongevallen']?.Relatiedocument?.VP || data['doorlopendereis']?.Relatiedocument?.VP;
        if (drVp?.VP_PCODE) {
          if (drVp.VP_STRAAT) vpData.VP_STRAAT = drVp.VP_STRAAT;
          if (drVp.VP_HUISNR) vpData.VP_HUISNR = Number(drVp.VP_HUISNR);
          if (drVp.VP_TOEVOEG) vpData.VP_TOEVOEG = drVp.VP_TOEVOEG;
          vpData.VP_PCODE = drVp.VP_PCODE;
          if (drVp.VP_PLAATS) vpData.VP_PLAATS = drVp.VP_PLAATS;
          vpData.VP_ADPSTAT = 'AD2';
        }
      }

      // Get payment info to add IBAN to VP
      const paymentInfo = paymentData?.paymentInfo || {};

      // Add IBAN to VP data if available (remove spaces for BFF)
      if (paymentInfo.iban) {
        vpData.VP_IBAN = paymentInfo.iban.replace(/\s/g, '');
      }

      // Build request document from quote data for each product
      // Start with the actual quote responses from each product

      const acceptanceData = getStepData('acceptance-questions') || {};
      const acceptanceFormData = acceptanceData?.formData;

      // Get quote data from acceptance-questions formData (which contains merged quote + payment + questions)
      const productQuotes: any[] = [];

      // Personenauto product
      if (selectedProducts?.personenauto && acceptanceFormData?.personenauto) {
        const personenautoData = acceptanceFormData.personenauto;
        const personenautoQuote: any = {};
        if (personenautoData.Contractdocument?.PP) {
          personenautoQuote.PP = { ...personenautoData.Contractdocument.PP };
          personenautoQuote.PP.PP_STPRV = 'N';
        }
        if (personenautoData.Contractdocument?.AL) {
          personenautoQuote.AL = { ...personenautoData.Contractdocument.AL };
        }
        productQuotes.push(sortAfdDocument(personenautoQuote));
      }

      if (selectedProducts?.woonhuis && acceptanceFormData?.woonhuis) {
        const woonhuisData = acceptanceFormData.woonhuis;

        // Get coverage selections from workflow data
        const woonhuisStepData = getStepData('woonhuis');
        const woonhuisDekkingen = woonhuisStepData?.Supportsection?.Dekkingen || {};

        // Unwrap Contractdocument structure to match BFF expected format
        const woonhuisQuote: any = {};
        if (woonhuisData.Contractdocument?.PP) {
          woonhuisQuote.PP = { ...woonhuisData.Contractdocument.PP };

          // Remove unselected coverage sections
          if (woonhuisDekkingen.Glas !== 'J') delete woonhuisQuote.PP.GP;
          if (woonhuisDekkingen.Verhuur !== 'J') delete woonhuisQuote.PP.VR;

          // Clean VW (conditions) array to remove unselected coverage terms
          if (woonhuisQuote.PP.VW && Array.isArray(woonhuisQuote.PP.VW)) {
            woonhuisQuote.PP.VW = woonhuisQuote.PP.VW.filter((vw: any) => {
              const vrwOm = vw.VW_VRWOM || '';
              if (vrwOm.includes('Glas') && woonhuisDekkingen.Glas !== 'J') return false;
              if (vrwOm.includes('Verhuur') && woonhuisDekkingen.Verhuur !== 'J') return false;
              return true;
            });
          }

          // Clean CL (clauses) array to remove unselected coverage clauses
          if (woonhuisQuote.PP.CL && Array.isArray(woonhuisQuote.PP.CL)) {
            woonhuisQuote.PP.CL = woonhuisQuote.PP.CL.filter((cl: any) => {
              const clausNm = cl.CL_CLAUSNM || '';
              if (clausNm.includes('Glas') && woonhuisDekkingen.Glas !== 'J') return false;
              if (clausNm.includes('Verhuur') && woonhuisDekkingen.Verhuur !== 'J') return false;
              return true;
            });
          }
        }
        // Add AL (Application) section with required fields
        woonhuisQuote.AL = {
          AL_APPLNM: appName,
          AL_URLNAAM: window.location.origin + '/',
          ...(woonhuisData.Contractdocument?.AL || woonhuisStepData?.Contractdocument?.AL || {}),
        };
        if (woonhuisData.Contractdocument?.NT) {
          woonhuisQuote.NT = [...woonhuisData.Contractdocument.NT];
        }

        // Strip Supportsection from quote (UI-only state, not allowed in request)
        delete woonhuisQuote.Supportsection;
        delete woonhuisQuote.PP?.Supportsection;

        // Set PP_RDNMUT (always "1" - Reden aanvraag/mutatie code)
        if (woonhuisQuote.PP && !woonhuisQuote.PP.PP_RDNMUT) {
          woonhuisQuote.PP.PP_RDNMUT = '1';
        }

        // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
        if (woonhuisQuote.PP) woonhuisQuote.PP.PP_EKST = policyAllocation.woonhuis;
        if (woonhuisQuote.PP) woonhuisQuote.PP.PP_STPRV = 'J';
        applyPpDefaults(woonhuisQuote.PP, WOONHUIS_PP_REQUEST_DEFAULTS);

        // Set PP_CONTROM from OP_GSS text (resolved via i18n)
        const woonhuisGssKey = GSS_CODE_TO_I18N_KEY[woonhuisQuote.PP?.OP?.OP_GSS];
        if (woonhuisQuote.PP && woonhuisGssKey) {
          woonhuisQuote.PP.PP_CONTROM = t(woonhuisGssKey);
        }

        // Strip fields not allowed in request (PP_TKRT/PP_TTSL/PP_BTP handled universally below).
        delete woonhuisQuote.PP?.PP_TKST;
        delete woonhuisQuote.PP?.PP_PKST;

        // Strip *_TTSL from coverage sections
        for (const section of ['OP', 'GP', 'VR']) {
          if (woonhuisQuote.PP?.[section]) {
            delete woonhuisQuote.PP[section][`${section}_TTSL`];
          }
        }

        // RI: whitelist per woonhuis.json schema
        if (woonhuisQuote.PP?.RI) {
          const allowedRI = new Set(['RI_ALGVRWD', 'RI_CGGDA', 'RI_EGD', 'RI_OGB', 'RI_OVRMED', 'RI_POLJUS', 'RI_STVOOP']);
          for (const key of Object.keys(woonhuisQuote.PP.RI)) {
            if (!allowedRI.has(key)) delete woonhuisQuote.PP.RI[key];
          }
        }

        // Default *_WPREMBP='4' on the Woonhuis coverage sections that define it.
        // Not GP: woonhuis.json's GP block has no GP_WPREMBP property and is
        // additionalProperties:false, so adding it would be invalid.
        if (woonhuisQuote.PP?.OP) {
          if (Array.isArray(woonhuisQuote.PP.OP)) woonhuisQuote.PP.OP.forEach((o: any) => { if (!o.OP_WPREMBP) o.OP_WPREMBP = '4'; });
          else if (!woonhuisQuote.PP.OP.OP_WPREMBP) woonhuisQuote.PP.OP.OP_WPREMBP = '4';
        }

        productQuotes.push(woonhuisQuote);
      }

      if (selectedProducts?.inboedel && acceptanceFormData?.inboedel) {
        const inboedelData = acceptanceFormData.inboedel;

        // Get coverage selections from workflow data
        const inboedelStepData = getStepData('inboedel');
        const inboedelDekkingen = inboedelStepData?.Supportsection?.Dekkingen || inboedelStepData?._uiState?.Dekkingen || {};

        // Unwrap Contractdocument structure to match BFF expected format
        const inboedelQuote: any = {};
        if (inboedelData.Contractdocument?.PP) {
          inboedelQuote.PP = { ...inboedelData.Contractdocument.PP };

          delete inboedelQuote.PP.CY;
          // Glas also goes when this address may not carry glass (inboedelGlassAllowed), whatever
          // Dekkingen says: the application document is built from stored step data, which
          // carries the applyProductDefaults GP block even when no calculation ever sent it.
          if (inboedelDekkingen.Glas !== 'J' || !inboedelGlassAllowed(config, selectedProducts)) delete inboedelQuote.PP.GP;
          if (inboedelDekkingen.Buitenshuis !== 'J') delete inboedelQuote.PP.BI;
          if (inboedelDekkingen.Mobieleelektronica !== 'J') delete inboedelQuote.PP.EC;
          keepSelectedEcRow(inboedelQuote.PP, inboedelDekkingen);
          if (inboedelDekkingen.Lijfsieraden !== 'J') delete inboedelQuote.PP.LS;
          if (inboedelDekkingen.Onbeperktnieuwwaarde !== 'J') delete inboedelQuote.PP.GA;

          if (inboedelQuote.PP.VW && Array.isArray(inboedelQuote.PP.VW)) {
            inboedelQuote.PP.VW = inboedelQuote.PP.VW.filter((vw: any) => {
              const vrwOm = vw.VW_VRWOM || '';
              if (vrwOm.includes('Cyber') && inboedelDekkingen.Cyber !== 'J') return false;
              if (vrwOm.includes('Glas') && inboedelDekkingen.Glas !== 'J') return false;
              if (vrwOm.includes('Buitenshuis') && inboedelDekkingen.Buitenshuis !== 'J') return false;
              if (vrwOm.includes('Elektronica') && inboedelDekkingen.Mobieleelektronica !== 'J') return false;
              if (vrwOm.includes('Lijfsieraden') && inboedelDekkingen.Lijfsieraden !== 'J') return false;
              if (vrwOm.includes('Verhuur') && inboedelDekkingen.Verhuur !== 'J') return false;
              if (vrwOm.includes('Eigenaar') && inboedelDekkingen.Huurdersbelang !== 'J') return false;
              if (vrwOm.includes('Bijzondere') && inboedelDekkingen.Bijzonderebezittingen !== 'J') return false;
              if (vrwOm.includes('nieuwwaarde') && inboedelDekkingen.Onbeperktnieuwwaarde !== 'J') return false;
              return true;
            });
          }

          // Clean CL (clauses) array to remove unselected coverage clauses
          if (inboedelQuote.PP.CL && Array.isArray(inboedelQuote.PP.CL)) {
            inboedelQuote.PP.CL = inboedelQuote.PP.CL.filter((cl: any) => {
              const clausNm = cl.CL_CLAUSNM || '';
              if (clausNm.includes('Cyber') && inboedelDekkingen.Cyber !== 'J') return false;
              if (clausNm.includes('Glas') && inboedelDekkingen.Glas !== 'J') return false;
              if (clausNm.includes('Buitenshuis') && inboedelDekkingen.Buitenshuis !== 'J') return false;
              if (clausNm.includes('Elektronica') && inboedelDekkingen.Mobieleelektronica !== 'J') return false;
              if (clausNm.includes('Lijfsieraden') && inboedelDekkingen.Lijfsieraden !== 'J') return false;
              if (clausNm.includes('Verhuur') && inboedelDekkingen.Verhuur !== 'J') return false;
              if (clausNm.includes('Eigenaar') && inboedelDekkingen.Huurdersbelang !== 'J') return false;
              if (clausNm.includes('Bijzondere') && inboedelDekkingen.Bijzonderebezittingen !== 'J') return false;
              if (clausNm.includes('nieuwwaarde') && inboedelDekkingen.Onbeperktnieuwwaarde !== 'J') return false;
              return true;
            });
          }
        }

        if (inboedelData.AL) {
          inboedelQuote.AL = { ...inboedelData.AL };
        }

        // === Schema compliance cleanup for inboedel final request ===
        if (inboedelQuote.PP) {
          // Remove non-schema PP properties (may be re-introduced by BFF merges)
          delete inboedelQuote.PP.WM;
          delete inboedelQuote.PP.VZ;
          delete inboedelQuote.PP.PP_MYNAAM;

          inboedelQuote.PP.PP_PPRC = Number(inboedelQuote.PP.PP_PPRC ?? 20);
          // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
          inboedelQuote.PP.PP_EKST = policyAllocation.inboedel;
          inboedelQuote.PP.PP_STPRV = 'J';
          if (!inboedelQuote.PP.PP_INCWIJZ) inboedelQuote.PP.PP_INCWIJZ = 'I';

          // Restore IB from _uiState if missing (InboedelScreen moves IB_INBVST to _uiState on save)
          if (!inboedelQuote.PP.IB) {
            const ibInbvst = inboedelStepData?._uiState?.IB_INBVST;
            if (ibInbvst) {
              inboedelQuote.PP.IB = { IB_INBVST: ibInbvst };
            }
          }

          // IP: keep as object (matches productservice/calculate response format)
          if (Array.isArray(inboedelQuote.PP.IP)) {
            inboedelQuote.PP.IP = inboedelQuote.PP.IP[0] || {};
          }
          if (inboedelQuote.PP.IP && !inboedelQuote.PP.IP.IP_GEBIED) {
            inboedelQuote.PP.IP.IP_GEBIED = 'W';
          }
          // Convert EC to array if present (schema: minItems 0, maxItems 1)
          if (inboedelQuote.PP.EC && !Array.isArray(inboedelQuote.PP.EC)) {
            inboedelQuote.PP.EC = [inboedelQuote.PP.EC];
          }

          // Ensure VP whitelist (schema only allows VP_GEBDAT and VP_PARZAKC in PP.VP)
          if (inboedelQuote.PP.VP) {
            const { VP_GEBDAT, VP_PARZAKC } = inboedelQuote.PP.VP;
            inboedelQuote.PP.VP = {
              ...(VP_PARZAKC ? { VP_PARZAKC } : { VP_PARZAKC: 'P' }),
              ...(VP_GEBDAT !== undefined ? { VP_GEBDAT: Number(VP_GEBDAT) } : {}),
            };
          }

          // Ensure BA cleanup + restore fields from _uiState
          if (inboedelQuote.PP.BA) {
            inboedelQuote.PP.BA = { ...inboedelQuote.PP.BA };
            delete inboedelQuote.PP.BA.BA_ZONNPAN;
            const uiState = inboedelStepData?._uiState || {};
            if (inboedelQuote.PP.BA.BA_MONUMEN === undefined && uiState.BA_MONUMEN !== undefined) {
              inboedelQuote.PP.BA.BA_MONUMEN = uiState.BA_MONUMEN;
            }
            if (inboedelQuote.PP.BA.BA_OPPWON === undefined && uiState.BA_OPPWON !== undefined) {
              inboedelQuote.PP.BA.BA_OPPWON = uiState.BA_OPPWON;
            }
            if (inboedelQuote.PP.BA.BA_HUISNR !== undefined) {
              inboedelQuote.PP.BA.BA_HUISNR = parseInt(String(inboedelQuote.PP.BA.BA_HUISNR), 10);
            }
          }

          // Ensure VR cleanup
          if (inboedelQuote.PP.VR) {
            delete inboedelQuote.PP.VR.VR_ERB;
            if (inboedelQuote.PP.VR.VR_PPRC !== undefined) {
              inboedelQuote.PP.VR.VR_PPRC = Number(inboedelQuote.PP.VR.VR_PPRC);
            }
          }

          // Type enforcement: PPRC fields must be numbers
          for (const section of ['BI', 'GA', 'GP', 'HU', 'KB', 'LS']) {
            if (inboedelQuote.PP[section]?.[`${section}_PPRC`] !== undefined) {
              inboedelQuote.PP[section][`${section}_PPRC`] = Number(inboedelQuote.PP[section][`${section}_PPRC`]);
            }
          }
          // Type enforcement: IP is object — BFF returns premium fields as strings
          if (inboedelQuote.PP.IP) {
            const ipFields = ['IP_BTP', 'IP_ERB', 'IP_NTP', 'IP_PPRC', 'IP_TASS', 'IP_TPP', 'IP_TTOT', 'IP_VERZSOM'];
            for (const f of ipFields) {
              if (inboedelQuote.PP.IP[f] !== undefined) inboedelQuote.PP.IP[f] = Number(inboedelQuote.PP.IP[f]);
            }
          }
          // Type enforcement: EC array items — BFF returns premium fields as strings
          if (Array.isArray(inboedelQuote.PP.EC)) {
            const ecFields = ['EC_BTP', 'EC_ERB', 'EC_NTP', 'EC_PPRC', 'EC_TASS', 'EC_TPP', 'EC_TTOT', 'EC_VERZSOM'];
            for (const ec of inboedelQuote.PP.EC) {
              for (const f of ecFields) {
                if (ec[f] !== undefined) ec[f] = Number(ec[f]);
              }
            }
          }
          // Type enforcement: BI object — BFF may return premium/verzsom fields as strings
          if (inboedelQuote.PP.BI) {
            const biFields = ['BI_BTP', 'BI_ERB', 'BI_NTP', 'BI_PPRC', 'BI_TASS', 'BI_TPP', 'BI_TTOT', 'BI_VERZSOM'];
            for (const f of biFields) {
              if (inboedelQuote.PP.BI[f] !== undefined) inboedelQuote.PP.BI[f] = Number(inboedelQuote.PP.BI[f]);
            }
          }
          // PP_CDUUMND must be number
          if (inboedelQuote.PP.PP_CDUUMND !== undefined) {
            inboedelQuote.PP.PP_CDUUMND = Number(inboedelQuote.PP.PP_CDUUMND);
          }
        }

        // AL: keep full data (AL_APPLNM, AL_URLNAAM, AL_VRWRKCD, BY array)
        if (inboedelData.Contractdocument?.AL) {
          inboedelQuote.AL = { ...inboedelData.Contractdocument.AL };
        }

        // NT (Notes)
        inboedelQuote.NT = inboedelData.Contractdocument?.NT || inboedelData.NT || [];
        if (inboedelData.XG) {
          inboedelQuote.XG = { ...inboedelData.XG };
        }
        // Supportsection, Relatiedocument, Divers are NOT included in inboedel calls

        // When both Woonhuis and Inboedel are selected, they share the same risk address
        // and get assigned the same FISHID by the BFF. This causes ANVA error 90470
        // "Fout bij schrijven bestand FISHNACO" when trying to write duplicate FISH records.
        // Solution: Remove FISH-related VP fields from Inboedel when Woonhuis is also selected,
        // so the BFF only generates one FISH-NABEVRAGING instruction per risk address.
        if (selectedProducts?.woonhuis && inboedelQuote.PP?.VP) {
          const { VP_FISHJN, VP_FISHJNO, VP_RELNUM, VP_NWKLNT, ...vpWithoutFish } = inboedelQuote.PP.VP;
          inboedelQuote.PP.VP = vpWithoutFish;
          if (import.meta.env.DEV) {
            console.log('[Confirmation] Stripped FISH fields from Inboedel VP to prevent duplicate FISHNACO writes');
          }
        }

        // Set PP_RDNMUT (always "1" - Reden aanvraag/mutatie code)
        if (inboedelQuote.PP && !inboedelQuote.PP.PP_RDNMUT) {
          inboedelQuote.PP.PP_RDNMUT = '1';
        }

        // Set PP_CONTROM from IP_GSS text (resolved via i18n)
        const ipForGss = inboedelQuote.PP?.IP;
        const inboedelGssKey = GSS_CODE_TO_I18N_KEY[ipForGss?.IP_GSS];
        if (inboedelQuote.PP && inboedelGssKey) {
          inboedelQuote.PP.PP_CONTROM = t(inboedelGssKey);
        }

        // RI: whitelist per inboedel.json schema
        if (inboedelQuote.PP?.RI) {
          const allowedRI = new Set(['RI_ALGVRWD', 'RI_CGGDA', 'RI_EGD', 'RI_OGB', 'RI_OVRMED', 'RI_POLJUS', 'RI_STVOOP']);
          for (const key of Object.keys(inboedelQuote.PP.RI)) {
            if (!allowedRI.has(key)) delete inboedelQuote.PP.RI[key];
          }
        }

        // Default *_WPREMBP='4' across all Inboedel coverage sections.
        if (inboedelQuote.PP?.IP && !inboedelQuote.PP.IP.IP_WPREMBP) inboedelQuote.PP.IP.IP_WPREMBP = '4';
        if (inboedelQuote.PP?.BI && !inboedelQuote.PP.BI.BI_WPREMBP) inboedelQuote.PP.BI.BI_WPREMBP = '4';
        if (inboedelQuote.PP?.HU && !inboedelQuote.PP.HU.HU_WPREMBP) inboedelQuote.PP.HU.HU_WPREMBP = '4';
        if (inboedelQuote.PP?.KB && !inboedelQuote.PP.KB.KB_WPREMBP) inboedelQuote.PP.KB.KB_WPREMBP = '4';
        if (inboedelQuote.PP?.LS && !inboedelQuote.PP.LS.LS_WPREMBP) inboedelQuote.PP.LS.LS_WPREMBP = '4';
        if (inboedelQuote.PP?.GA && !inboedelQuote.PP.GA.GA_WPREMBP) inboedelQuote.PP.GA.GA_WPREMBP = '4';
        if (inboedelQuote.PP?.GP && !inboedelQuote.PP.GP.GP_WPREMBP) inboedelQuote.PP.GP.GP_WPREMBP = '4';
        if (Array.isArray(inboedelQuote.PP?.EC)) inboedelQuote.PP.EC.forEach((ec: any) => { if (!ec.EC_WPREMBP) ec.EC_WPREMBP = '4'; if (!ec.EC_GEBIED) ec.EC_GEBIED = 'W'; });

        productQuotes.push(inboedelQuote);
      }

      if (selectedProducts?.aansprakelijkheid && acceptanceFormData?.avp) {
        const avpData = acceptanceFormData.avp;
        const avpStepData = getStepData('aansprakelijkheid');

        // Unwrap Contractdocument structure to match BFF expected format
        const avpQuote: any = {};
        if (avpData.Contractdocument?.PP) {
          avpQuote.PP = { ...avpData.Contractdocument.PP };
        }

        // Ensure VP is populated from step data if missing
        if ((!avpQuote.PP?.VP || Object.keys(avpQuote.PP.VP).length === 0) && avpStepData?.Contractdocument?.PP?.VP) {
          avpQuote.PP.VP = { ...avpStepData.Contractdocument.PP.VP };
        }

        // Ensure VZ is populated from step data if missing
        if (!avpQuote.PP?.VZ && avpStepData?.Contractdocument?.PP?.VZ) {
          avpQuote.PP.VZ = { ...avpStepData.Contractdocument.PP.VZ };
        }

        // Ensure VL is populated from step data if missing
        if ((!avpQuote.PP?.VL || Object.keys(avpQuote.PP.VL).length === 0) && avpStepData?.Contractdocument?.PP?.VL) {
          avpQuote.PP.VL = { ...avpStepData.Contractdocument.PP.VL };
        }

        // Ensure PP_INCWIJZ is set
        if (!avpQuote.PP?.PP_INCWIJZ && avpStepData?.Contractdocument?.PP?.PP_INCWIJZ) {
          avpQuote.PP.PP_INCWIJZ = avpStepData.Contractdocument.PP.PP_INCWIJZ;
        }

        // Ensure PP_PRODUCT is set
        if (!avpQuote.PP?.PP_PRODUCT) {
          avpQuote.PP.PP_PRODUCT = avpQuote.PP?.PP_PRODNM || avpStepData?.Contractdocument?.PP?.PP_PRODUCT;
        }

        // Replace VW items entirely from step data (core strips VW_URL, VW_BRONVRW, VW_VOLGNUM)
        if (avpStepData?.Contractdocument?.PP?.VW && Array.isArray(avpStepData.Contractdocument.PP.VW)) {
          avpQuote.PP.VW = avpStepData.Contractdocument.PP.VW.map((vw: any) => ({
            ...vw,
            VW_VRWRKCD: vw.VW_VRWRKCD ?? '-',
          }));
        } else if (Array.isArray(avpQuote.PP?.VW)) {
          // Ensure VW_VRWRKCD is present on existing VW items
          avpQuote.PP.VW = avpQuote.PP.VW.map((vw: any) => ({
            ...vw,
            VW_VRWRKCD: vw.VW_VRWRKCD ?? '-',
          }));
        }

        // Add AL (Application) section with required fields
        avpQuote.AL = {
          AL_APPLNM: appName,
          AL_URLNAAM: window.location.origin + '/',
          ...(avpData.AL || {}),
        };
        // Strip fields not needed for AVP request
        delete avpQuote.AL?.AL_VRWRKCD;
        delete avpQuote.PP?.PP_VRWRKCD;
        delete avpQuote.PP?.PP_RDNMUT;
        delete avpQuote.PP?.BA;
        delete avpQuote.NT;
        if (avpQuote.PP?.DA) {
          delete avpQuote.PP.DA.DA_VRWRKCD;
          delete avpQuote.PP.DA.DA_INGDAT;
        }
        if (avpQuote.PP?.VP) {
          delete avpQuote.PP.VP.VP_VRWRKCD;
          delete avpQuote.PP.VP.VP_EMAIL;
          delete avpQuote.PP.VP.VP_GESLACH;
          delete avpQuote.PP.VP.VP_TELNUM;
        }

        // Set PP_WPREMBP (always 4)
        if (!avpQuote.PP?.PP_WPREMBP) {
          avpQuote.PP.PP_WPREMBP = '4';
        }

        if (avpQuote.PP) {
          avpQuote.PP.PP_STPRV = 'J';
        }

        // DA_WPREMBP default
        if (avpQuote.PP?.DA && !avpQuote.PP.DA.DA_WPREMBP) {
          avpQuote.PP.DA.DA_WPREMBP = '4';
        }

        // DA_VERZSOM default — mirrors AVP screen default (5,000,000)
        if (avpQuote.PP?.DA && avpQuote.PP.DA.DA_VERZSOM === undefined) {
          avpQuote.PP.DA.DA_VERZSOM = 5000000;
        }

        // PP_CONTROM from DA_GSS (calculation enrich sets it; mirror it here so the request carries it too)
        if (!avpQuote.PP?.PP_CONTROM) {
          const avpGssKey = GSS_CODE_TO_I18N_KEY[avpQuote.PP?.DA?.DA_GSS];
          if (avpGssKey) avpQuote.PP.PP_CONTROM = t(avpGssKey);
        }

        // PP_TPAKKRT default
        if (avpQuote.PP?.PP_TPAKKRT === undefined) {
          avpQuote.PP.PP_TPAKKRT = 0;
        }


        // Ensure VP_HUISNR is a number
        if (avpQuote.PP?.VP?.VP_HUISNR !== undefined) {
          avpQuote.PP.VP.VP_HUISNR = Number(avpQuote.PP.VP.VP_HUISNR);
        }

        // Restore BY from step data if not present in AL
        if (!avpQuote.AL?.BY && avpStepData?.Contractdocument?.AL?.BY) {
          avpQuote.AL.BY = avpStepData.Contractdocument.AL.BY;
        }
        // Fix BY_BYLSRT: must be string per schema
        if (avpQuote.AL?.BY && Array.isArray(avpQuote.AL.BY)) {
          avpQuote.AL.BY = avpQuote.AL.BY.map((item: any) => ({
            ...item,
            ...(item.BY_BYLSRT !== undefined && typeof item.BY_BYLSRT !== 'string'
              ? { BY_BYLSRT: String(item.BY_BYLSRT) }
              : {}),
          }));
        }

        // PP_EKST - 0 at product level; charged once at package level (PK_TINV)
        if (avpQuote.PP) avpQuote.PP.PP_EKST = policyAllocation.aansprakelijkheid;

        if (!avpQuote.PP.RI) avpQuote.PP.RI = {};
        {
          const allowedRI = new Set(['RI_ALGVRWD', 'RI_CGGDA', 'RI_EGD', 'RI_OGB', 'RI_OVRMED', 'RI_POLJUS', 'RI_STVOOP', 'US_AANSPRP']);
          for (const key of Object.keys(avpQuote.PP.RI)) {
            if (!allowedRI.has(key)) delete avpQuote.PP.RI[key];
          }
          const acceptanceAnswers = acceptanceData?.answers || {};
          for (const q of ['RI_EGD', 'RI_OGB', 'RI_POLJUS', 'RI_OVRMED', 'RI_CGGDA', 'RI_STVOOP', 'US_AANSPRP']) {
            avpQuote.PP.RI[q] = acceptanceAnswers[q] || avpQuote.PP.RI[q] || 'N';
          }
        }

        // Ensure TP exists
        if (!avpQuote.PP.TP) avpQuote.PP.TP = {};

        // Ensure VE with VE_MYAAND
        if (!avpQuote.PP.VE) avpQuote.PP.VE = { VE_MYAAND: 'IP01' };
        else if (!avpQuote.PP.VE.VE_MYAAND) avpQuote.PP.VE.VE_MYAAND = 'IP01';

        // Ensure VL with VL_MYAAND
        if (!avpQuote.PP.VL) avpQuote.PP.VL = { VL_MYAAND: avpQuote.PP.PP_MYAAND || 'R515' };
        else if (!avpQuote.PP.VL.VL_MYAAND) avpQuote.PP.VL.VL_MYAAND = avpQuote.PP.PP_MYAAND || 'R515';

        // Ensure VZ has required fields
        if (avpQuote.PP.VZ) {
          if (!avpQuote.PP.VZ.VZ_RELSRTC) avpQuote.PP.VZ.VZ_RELSRTC = '1';
          if (avpQuote.PP.VZ.VZ_GEBDAT === undefined && avpQuote.PP.VP?.VP_GEBDAT !== undefined) {
            avpQuote.PP.VZ.VZ_GEBDAT = avpQuote.PP.VP.VP_GEBDAT;
          }
        }

        // Ensure PP_TPP exists (will not be deleted for AVP, see below)
        if (avpQuote.PP.PP_TPP === undefined) avpQuote.PP.PP_TPP = 0;

        productQuotes.push(avpQuote);
      }

      // Rechtsbijstand product
      if (selectedProducts?.rechtsbijstand && acceptanceFormData?.rechtsbijstand) {
        const rechtsbijstandData = acceptanceFormData.rechtsbijstand;
        const rechtsbijstandQuote: any = {};
        if (rechtsbijstandData.Contractdocument?.PP) {
          rechtsbijstandQuote.PP = { ...rechtsbijstandData.Contractdocument.PP };
          // Ensure required fields are set
          rechtsbijstandQuote.PP.PP_EXTERN = 'RB';
          if (!rechtsbijstandQuote.PP.PP_BRANCHE) rechtsbijstandQuote.PP.PP_BRANCHE = '092';
          if (!rechtsbijstandQuote.PP.PP_MYAAND) rechtsbijstandQuote.PP.PP_MYAAND = 'P020';
          if (!rechtsbijstandQuote.PP.PP_HANDACC) rechtsbijstandQuote.PP.PP_HANDACC = 'N';
          rechtsbijstandQuote.PP.PP_STPRV = 'J';
          // PP_EKST - one-time poliskosten (cap-aware allocation across the package)
          rechtsbijstandQuote.PP.PP_EKST = policyAllocation.rechtsbijstand;
          if (!rechtsbijstandQuote.PP.PP_BETTERM) rechtsbijstandQuote.PP.PP_BETTERM = 1;
          applyPpDefaults(rechtsbijstandQuote.PP, RBV_PP_REQUEST_DEFAULTS);
          if (!rechtsbijstandQuote.PP.VE) rechtsbijstandQuote.PP.VE = { VE_MYAAND: 'K781', VE_VRWRKCD: '0' };
          else if (!rechtsbijstandQuote.PP.VE.VE_VRWRKCD) rechtsbijstandQuote.PP.VE.VE_VRWRKCD = '0';
          if (!rechtsbijstandQuote.PP.CL) rechtsbijstandQuote.PP.CL = [];
          if (rechtsbijstandQuote.PP.PP_AFWAFST === undefined) rechtsbijstandQuote.PP.PP_AFWAFST = '';
          if (rechtsbijstandQuote.PP.PP_IBAN === undefined) rechtsbijstandQuote.PP.PP_IBAN = '';
          // Strip internal fields
          delete rechtsbijstandQuote.PP._rpEigendom;
          // Build BA array from VP address (insured person address)
          const rbVp = rechtsbijstandQuote.PP.VP || vpData || {};
          const rbBaItem: any = {};
          if (rbVp.VP_PCODE) rbBaItem.BA_PCODE = rbVp.VP_PCODE;
          if (rbVp.VP_HUISNR) rbBaItem.BA_HUISNR = Number(rbVp.VP_HUISNR);
          if (rbVp.VP_TOEVOEG) rbBaItem.BA_TOEVOEG = rbVp.VP_TOEVOEG;
          if (rbVp.VP_STRAAT) rbBaItem.BA_STRAAT = rbVp.VP_STRAAT;
          if (rbVp.VP_PLAATS) rbBaItem.BA_PLAATS = rbVp.VP_PLAATS;
          rechtsbijstandQuote.PP.BA = Object.keys(rbBaItem).length > 0 ? [rbBaItem] : [];

          // Strip unselected coverages - only include what was actually chosen
          const rbDekkingen = getStepData('rechtsbijstand')?.Supportsection?.Dekkingen || {};
          if (rbDekkingen.Verkeer !== 'J') {
            delete rechtsbijstandQuote.PP.VK;
          } else if (rechtsbijstandQuote.PP.VK) {
            delete rechtsbijstandQuote.PP.VK.VK_GEZSAMST;
          }
          if (rbDekkingen.Inkomen !== 'J') rechtsbijstandQuote.PP.IN = { IN_EIGBVAK: RBV_IN_EIGBVAK_DEFAULT };
          else if (rechtsbijstandQuote.PP.IN && !rechtsbijstandQuote.PP.IN.IN_EIGBVAK) rechtsbijstandQuote.PP.IN.IN_EIGBVAK = RBV_IN_EIGBVAK_DEFAULT;
          if (rbDekkingen.Fiscaal_Vermogen !== 'J') delete rechtsbijstandQuote.PP.FV;
          // RP: filter to only selected dekkingen
          const rpCodeToDekkingKey: Record<string, string> = {
            '9520': 'Eigendom_Verhuur_Onroerende_Zaken',
            '9530': 'Statutair_directeur',
            '9531': 'Bestuurder_Vereniging_van_Eigenaars',
            '9572': 'Niet_consumentenkoop',
            '9589': 'Financieel_onvermogen',
          };
          if (Array.isArray(rechtsbijstandQuote.PP.RP)) {
            rechtsbijstandQuote.PP.RP = rechtsbijstandQuote.PP.RP.filter(
              (rp: any) => {
                const key = rpCodeToDekkingKey[rp?.RP_CODE];
                return key && rbDekkingen[key] === 'J';
              }
            );
            if (rechtsbijstandQuote.PP.RP.length === 0) delete rechtsbijstandQuote.PP.RP;
          }

          // Coerce numeric fields from strings to numbers (BFF returns strings for RP, FV, IN, VK, WC)
          const coerceRbNumericFields = (obj: any): void => {
            if (!obj || typeof obj !== 'object') return;
            if (Array.isArray(obj)) { obj.forEach(coerceRbNumericFields); return; }
            const numericSuffixes = ['_BTP', '_NTP', '_TPP', '_TASS', '_TTOT', '_TKRT', '_ERB',
              '_PPRC', '_VERZSOM', '_VZBFRNS', '_EIGBVAK', '_VOLGNUM', '_VBE', '_VGAVRH', '_VWE',
              '_HVVDAT', '_INGDAT', '_EXPDAT', '_PVADAT', '_BETTERM', '_CDUUMND', '_EKST', '_TTPPRV',
              '_PKST', '_TTSL', '_PKRTVMP', '_TPKRTVM', '_WPREMBP'];
            for (const key of Object.keys(obj)) {
              const val = obj[key];
              if (typeof val === 'string') {
                const suffix = key.substring(key.lastIndexOf('_'));
                if (numericSuffixes.includes(suffix)) {
                  const num = Number(val);
                  if (!isNaN(num)) obj[key] = num;
                }
              } else if (val && typeof val === 'object') {
                coerceRbNumericFields(val);
              }
            }
          };
          coerceRbNumericFields(rechtsbijstandQuote.PP);
        }
        // AL: include AL_APPLNM, AL_URLNAAM and BY
        rechtsbijstandQuote.AL = {
          AL_APPLNM: appName,
          AL_URLNAAM: window.location.origin + '/',
        };
        if (Array.isArray(rechtsbijstandData.Contractdocument?.AL?.BY)) {
          rechtsbijstandQuote.AL.BY = rechtsbijstandData.Contractdocument.AL.BY.map((item: any) => ({
            ...item,
            ...(item.BY_BYLSRT !== undefined && typeof item.BY_BYLSRT === 'string'
              ? { BY_BYLSRT: Number(item.BY_BYLSRT) }
              : {}),
          }));
        }
        // RI: whitelist per rechtsbijstand.json schema
        if (rechtsbijstandQuote.PP?.RI) {
          const allowedRI = new Set(['RI_ALGVRWD', 'RI_BGP', 'RI_BYZVRWE', 'RI_DGP', 'RI_EGD', 'RI_GEWEIGE', 'RI_NGP', 'RI_STRAFRV']);
          for (const key of Object.keys(rechtsbijstandQuote.PP.RI)) {
            if (!allowedRI.has(key)) delete rechtsbijstandQuote.PP.RI[key];
          }
        }

        // Default *_WPREMBP='4' for Rechtsbijstand WC coverage.
        if (rechtsbijstandQuote.PP?.WC && !rechtsbijstandQuote.PP.WC.WC_WPREMBP) {
          rechtsbijstandQuote.PP.WC.WC_WPREMBP = '4';
        }

        productQuotes.push(rechtsbijstandQuote);
      }

      if (selectedProducts?.ongevallen && acceptanceFormData?.ongevallen) {
        const ogData = acceptanceFormData.ongevallen;
        const ogQuote: any = {};
        if (ogData.Contractdocument?.PP) {
          ogQuote.PP = { ...ogData.Contractdocument.PP };
          ogQuote.PP.PP_MYAAND = ONGEVALLEN_MYAAND;
          ogQuote.PP.PP_MYNAAM = ONGEVALLEN_MYAAND;
          if (!ogQuote.PP.PP_HANDACC) ogQuote.PP.PP_HANDACC = 'N';
          if (!ogQuote.PP.PP_SLOTAKK) ogQuote.PP.PP_SLOTAKK = 'N';
          if (!ogQuote.PP.PP_STPRV) ogQuote.PP.PP_STPRV = 'J';
          if (!ogQuote.PP.PP_POLPKJN) ogQuote.PP.PP_POLPKJN = 'J';
          applyPpDefaults(ogQuote.PP, ONGEVALLEN_PP_REQUEST_DEFAULTS);
          ogQuote.PP.PP_BETTERM = Number(ONGEVALLEN_DEFAULTS.PP_BETTERM);
          ogQuote.PP.PP_EKST = policyAllocation.ongevallen;
          if (ogQuote.PP.PP_IBAN === undefined) ogQuote.PP.PP_IBAN = '';
          if (!ogQuote.PP.VE) ogQuote.PP.VE = { VE_MYAAND: ONGEVALLEN_CARRIER };
          else ogQuote.PP.VE.VE_MYAAND = ONGEVALLEN_CARRIER;
          if (!ogQuote.PP.VL) ogQuote.PP.VL = { VL_MYAAND: ONGEVALLEN_CARRIER };
          else ogQuote.PP.VL.VL_MYAAND = ONGEVALLEN_CARRIER;
          if (!ogQuote.PP.CL) ogQuote.PP.CL = [];
          normalizeOngevallenVwCodes(ogQuote);
          ogQuote.PP.OD = restoreOngevallenInputs(ogQuote.PP.OD, data['ongevallen']?.values);
          ogQuote.PP.VZ = restoreOngevallenInsured(ogQuote.PP.VZ, {
            lastName: clientInfo.lastName,
            initials: clientInfo.initials,
            gebdat: vpData.VP_GEBDAT,
          });

          const ogVp = ogData.Relatiedocument?.VP || ogData.Contractdocument?.PP?.VP || {};
          const ogContractVp = ogData.Contractdocument?.PP?.VP || {};
          if (ogVp.VP_PCODE) {
            const hn = ogVp.VP_HUISNR;
            ogQuote.PP.VP = {
              VP_PCODE: ogVp.VP_PCODE,
              VP_STRAAT: ogVp.VP_STRAAT ?? '',
              VP_PLAATS: ogVp.VP_PLAATS ?? '',
              VP_TOEVOEG: ogVp.VP_TOEVOEG ?? '',
              VP_HUISNR:
                hn != null && String(hn).trim() !== '' && !isNaN(Number(hn)) ? Number(hn) : hn ?? '',
              // Policyholder identity: the contract VP's own schema fields (naam/geboortedatum).
              ...(ogContractVp.VP_ANAAM ? { VP_ANAAM: ogContractVp.VP_ANAAM } : {}),
              ...(ogContractVp.VP_GEBDAT ? { VP_GEBDAT: ogContractVp.VP_GEBDAT } : {}),
            };
          }
        }
        ogQuote.AL = {
          AL_APPLNM: appName,
          AL_URLNAAM: window.location.origin + '/',
        };
        // Bijlagen (voorwaarden links) forwarded like DR; the request wants BY_BYLSRT numeric.
        if (Array.isArray(ogData.Contractdocument?.AL?.BY)) {
          ogQuote.AL.BY = ogData.Contractdocument.AL.BY.map((item: any) => ({
            ...item,
            ...(item.BY_BYLSRT !== undefined && typeof item.BY_BYLSRT === 'string'
              ? { BY_BYLSRT: Number(item.BY_BYLSRT) }
              : {}),
          }));
        }
        // AFD convention: alphabetically sorted keys on the final-request document too.
        productQuotes.push(sortAfdDocument(ogQuote));
      }

      // Doorlopende reis (DFD/K781): build the quote from the accepted DR document. DR has no
      // risk-address BA (travel uses the policyholder VP), no coverage stripping, and PP_STPRV='J'.
      if (selectedProducts?.doorlopendereis && acceptanceFormData?.doorlopendereis) {
        const drData = acceptanceFormData.doorlopendereis;
        const drQuote: any = {};
        if (drData.Contractdocument?.PP) {
          drQuote.PP = { ...drData.Contractdocument.PP };
          drQuote.PP.PP_MYAAND = DOORLOPENDE_REIS_MYAAND;
          drQuote.PP.PP_MYNAAM = DOORLOPENDE_REIS_MYAAND;
          if (!drQuote.PP.PP_HANDACC) drQuote.PP.PP_HANDACC = 'N';
          if (!drQuote.PP.PP_SLOTAKK) drQuote.PP.PP_SLOTAKK = 'N';
          if (!drQuote.PP.PP_STPRV) drQuote.PP.PP_STPRV = 'J';
          if (!drQuote.PP.PP_POLPKJN) drQuote.PP.PP_POLPKJN = 'J';
          applyPpDefaults(drQuote.PP, DOORLOPENDE_REIS_PP_REQUEST_DEFAULTS);
          if (!drQuote.PP.PP_BETTERM) drQuote.PP.PP_BETTERM = 1;
          drQuote.PP.PP_EKST = policyAllocation.doorlopendereis;
          if (drQuote.PP.PP_IBAN === undefined) drQuote.PP.PP_IBAN = '';
          if (!drQuote.PP.VE) drQuote.PP.VE = { VE_MYAAND: DOORLOPENDE_REIS_CARRIER };
          else drQuote.PP.VE.VE_MYAAND = DOORLOPENDE_REIS_CARRIER;
          if (!drQuote.PP.VL) drQuote.PP.VL = { VL_MYAAND: DOORLOPENDE_REIS_CARRIER };
          else drQuote.PP.VL.VL_MYAAND = DOORLOPENDE_REIS_CARRIER;
          if (!drQuote.PP.CL) drQuote.PP.CL = [];
          if (drQuote.PP.DR) drQuote.PP.DR = restoreDoorlopendeReisInputs(drQuote.PP.DR);

          const drVp = drData.Relatiedocument?.VP || drData.Contractdocument?.PP?.VP || {};
          if (drVp.VP_PCODE) {
            const hn = drVp.VP_HUISNR;
            drQuote.PP.VP = {
              VP_PCODE: drVp.VP_PCODE,
              VP_STRAAT: drVp.VP_STRAAT ?? '',
              VP_PLAATS: drVp.VP_PLAATS ?? '',
              VP_TOEVOEG: drVp.VP_TOEVOEG ?? '',
              VP_HUISNR:
                hn != null && String(hn).trim() !== '' && !isNaN(Number(hn)) ? Number(hn) : hn ?? '',
            };
          }
        }
        drQuote.AL = {
          AL_APPLNM: appName,
          AL_URLNAAM: window.location.origin + '/',
        };
        if (Array.isArray(drData.Contractdocument?.AL?.BY)) {
          drQuote.AL.BY = drData.Contractdocument.AL.BY.map((item: any) => ({
            ...item,
            ...(item.BY_BYLSRT !== undefined && typeof item.BY_BYLSRT === 'string'
              ? { BY_BYLSRT: Number(item.BY_BYLSRT) }
              : {}),
          }));
        }
        productQuotes.push(drQuote);
      }

      // Build request structure with all product quotes
      // Check enriched document to determine if BFF used Contract or Pakket structure
      let pakketMantel = null;
      let usePakketStructure = false;
      try {
        const enrichedDoc = sessionStorage.getItem('enriched_calculation_document');
        if (enrichedDoc) {
          const doc = JSON.parse(enrichedDoc);
          const pakketOnderdeel = doc?.Pakket?.[0]?.Onderdeel || [];
          usePakketStructure = pakketOnderdeel.length > 0;

          if (usePakketStructure) {
            pakketMantel = doc?.Pakket?.[0]?.Mantel || null;

            // Set PK_RDNMUT (always "1" - Reden aanvraag/mutatie code)
            if (pakketMantel?.PK && !pakketMantel.PK.PK_RDNMUT) {
              pakketMantel.PK.PK_RDNMUT = '1';
            }

            // Remove VP from Pakket Mantel to prevent duplicate FISHNACO writes
            if (pakketMantel?.VP) {
              const { VP, ...mantelWithoutVP } = pakketMantel;
              pakketMantel = mantelWithoutVP;
            }

            // Strip fields not allowed in request from PK
            if (pakketMantel?.PK) {
              delete pakketMantel.PK.PK_PKST;
            }
          }

          // Merge corrected premium values from enriched document into productQuotes.
          // CRITICAL: identify product by its DISCRIMINATING section, not just "has OP". Inboedel
          // has BOTH IP (base) and OP (Eigenaarsbelang extra coverage); Woonhuis has only OP
          // (Opstal main coverage). Checking OP first lets an Inboedel quote match the Woonhuis
          // enriched product, after which the OP/IP/EC/… premiums get copied across products and
          // Inboedel's OP_BTP/NTP/TTOT end up holding Woonhuis's building-premium values in the
          // outgoing request. Order: IP → OP-without-IP → DA-without-IP-or-OP → OB → RB.
          const enrichedProducts = usePakketStructure ? pakketOnderdeel : (doc?.Contract || []);
          productQuotes.forEach((quote: any) => {
            let enrichedProduct = null;
            if (quote.PP?.IP) {
              enrichedProduct = enrichedProducts.find((p: any) => p.PP?.IP);
            } else if (quote.PP?.OP) {
              enrichedProduct = enrichedProducts.find((p: any) => p.PP?.OP && !p.PP?.IP);
            } else if (quote.PP?.DA) {
              enrichedProduct = enrichedProducts.find((p: any) => p.PP?.DA && !p.PP?.IP && !p.PP?.OP);
            } else if (quote.PP?.OB) {
              enrichedProduct = enrichedProducts.find((p: any) => p.PP?.OB);
            } else if (quote.PP?.PP_EXTERN === 'RB') {
              enrichedProduct = enrichedProducts.find((p: any) => p.PP?.PP_EXTERN === 'RB');
            } else if (quote.PP?.DR || quote.PP?.OD) {
              enrichedProduct = enrichedProducts.find((p: any) => p.PP?.DR || p.PP?.OD);
            }

            if (enrichedProduct?.PP) {
              // PP_BTP intentionally excluded — request payload sends premium amounts at the
              // section/DA level only; PP-level BTP/TKRT/TTSL are stripped in the final forEach.
              const premiumFields = ['PP_NTP', 'PP_TPP', 'PP_TASS', 'PP_TTOT', 'PP_NTPVPK'];
              premiumFields.forEach(field => {
                if (enrichedProduct.PP[field] !== undefined) {
                  quote.PP[field] = enrichedProduct.PP[field];
                }
              });

              // Coverage-level premium sync. Without this the request carries discounted PP
              // totals but undiscounted coverage amounts (BI/EC/IP/DA/OP/…), so
              // sum(coverage_NTP) ≠ PP_NTP and PP_TTOT drifts by a cent from the rounded sum.
              // Pull the discounted coverage premiums from the enriched document so the payload
              // is internally consistent with what the summary screen showed.
              const coverageKeys = ['BI', 'CA', 'CG', 'DA', 'EC', 'GA', 'GP', 'HU', 'IP', 'KB', 'LS', 'OP', 'PH', 'RP', 'SV', 'VR', 'WA'];
              const premiumSuffixes = ['_NTP', '_BTP', '_TPP', '_TASS', '_TTOT'];
              for (const key of coverageKeys) {
                const enrichedSec = enrichedProduct.PP[key];
                const quoteSec = quote.PP[key];
                if (!enrichedSec || !quoteSec) continue;
                const enrichedIsArr = Array.isArray(enrichedSec);
                const quoteIsArr = Array.isArray(quoteSec);
                if (enrichedIsArr && quoteIsArr) {
                  for (let i = 0; i < quoteSec.length && i < enrichedSec.length; i++) {
                    for (const suf of premiumSuffixes) {
                      const field = `${key}${suf}`;
                      if (enrichedSec[i]?.[field] !== undefined) quoteSec[i][field] = enrichedSec[i][field];
                    }
                  }
                } else if (!enrichedIsArr && !quoteIsArr) {
                  for (const suf of premiumSuffixes) {
                    const field = `${key}${suf}`;
                    if (enrichedSec[field] !== undefined) quoteSec[field] = enrichedSec[field];
                  }
                }
              }
            }

            // AVP: BFF rounds PP_* to 2 decimals during roundtrips but preserves DA_* precision.
            // Re-align PP with DA after the enriched merge so the request body carries precise
            // values (3.145, not BFF-rounded 3.15) matching the DA section. PP_BTP is intentionally
            // omitted — the request keeps premium at DA-level only.
            if (quote.PP?.DA && !quote.PP.IP && !quote.PP.OP && !quote.PP.OB && quote.PP?.PP_EXTERN !== 'RB') {
              const da = quote.PP.DA;
              if (da.DA_NTP !== undefined) {
                quote.PP.PP_NTP = Number(da.DA_NTP);
                quote.PP.PP_NTPVPK = Number(da.DA_NTP);
              }
              if (da.DA_TPP !== undefined) quote.PP.PP_TPP = Number(da.DA_TPP);
              if (da.DA_TASS !== undefined) quote.PP.PP_TASS = Number(da.DA_TASS);
              if (da.DA_TTOT !== undefined) quote.PP.PP_TTOT = Number(da.DA_TTOT);
            }

            // AVP: BFF rounds PP_* to 2 decimals during roundtrips but preserves DA_* precision.
            // Re-align PP with DA after the enriched merge so the request body carries precise
            // values (PP_BTP 3.145, not BFF-rounded 3.15) matching the DA section.
            if (quote.PP?.DA && !quote.PP.IP && !quote.PP.OP && !quote.PP.OB && quote.PP?.PP_EXTERN !== 'RB') {
              const da = quote.PP.DA;
              if (da.DA_BTP !== undefined) quote.PP.PP_BTP = Number(da.DA_BTP);
              if (da.DA_NTP !== undefined) {
                quote.PP.PP_NTP = Number(da.DA_NTP);
                quote.PP.PP_NTPVPK = Number(da.DA_NTP);
              }
              if (da.DA_TPP !== undefined) quote.PP.PP_TPP = Number(da.DA_TPP);
              if (da.DA_TASS !== undefined) quote.PP.PP_TASS = Number(da.DA_TASS);
              if (da.DA_TTOT !== undefined) quote.PP.PP_TTOT = Number(da.DA_TTOT);
            }
          });
        }
      } catch (error) {
        console.warn('[Confirmation] Failed to get enriched document:', error);
      }

      // Build request matching the structure the BFF used (Contract or Pakket)
      const requestDocument: any = {
        Relatiemantel: {
          VP: vpData,
          AL: {
            AL_APPLNM: appName,
            AL_URLNAAM: window.location.origin + '/'
          }
        },
      };

      if (usePakketStructure) {
        // BFF put products in Pakket - use Pakket structure for request
        requestDocument.Contract = [];
        requestDocument.Pakket = [
          {
            ...(pakketMantel ? { Mantel: pakketMantel } : {}),
            Onderdeel: productQuotes
          }
        ];
      } else {
        // BFF kept products in Contract - use Contract structure for request
        requestDocument.Contract = productQuotes;
      }


      // The product quotes already contain:
      // - Original quote/calculation data from BFF
      // - Payment information (IBAN, account holder, start date)
      // - Acceptance questions (QA array)
      // - Terms acceptance (RI_ALGVRWD)
      // - All BFF-generated fields (PP_PRODNM, PP_MYAAND, PP_MYNAAM, etc.)

      // Just ensure terms acceptance is up-to-date in each product
      // Also strip PP_TINV - BFF returns it but schema does not allow it
      productQuotes.forEach((quote: any) => {
        if (quote.PP?.RI) {
          quote.PP.RI.RI_ALGVRWD = paymentInfo.acceptTerms ? 'J' : 'N';
        }
        if (quote.PP) {
          delete quote.PP.PP_TINV;

          // PP-level premium aggregates are not sent in the request — coverage/DA-level
          // values are the source of truth. Strip across all 5 products.
          delete quote.PP.PP_BTP;
          delete quote.PP.PP_TKRT;
          delete quote.PP.PP_TTSL;

          // Ensure QS with QA array exists on all products (required by request endpoint)
          if (!quote.PP.QS) quote.PP.QS = { QA: [] };
          else if (!quote.PP.QS.QA) quote.PP.QS.QA = [];
        }

        // Personenauto-specific: clean non-schema fields for request endpoint
        if (quote.PP?.OB) {
          const pp = quote.PP;

          // AL: clean up but keep AL_APPLNM and AL_URLNAAM
          if (quote.AL) {
            delete quote.AL.AL_VRWRKCD;
            delete quote.AL.AL_CPNAAM;
            // BY items: whitelist
            if (Array.isArray(quote.AL.BY)) {
              const allowedBY = new Set(['BY_BYLOMS', 'BY_BYLSRT', 'BY_URLBY']);
              quote.AL.BY = quote.AL.BY.map((item: any) => {
                const cleaned: any = {};
                for (const key of Object.keys(item)) {
                  if (allowedBY.has(key)) cleaned[key] = item[key];
                }
                return cleaned;
              });
            }
          }

          // Strip XG from Contractdocument level (not allowed by schema)
          if (quote.XG) delete quote.XG;

          // BS: whitelist (schema requires all personal + address fields)
          if (pp.BS) {
            const allowedBS = new Set([
              'BS_ANAAM', 'BS_GEBDAT', 'BS_GESLACH', 'BS_HUISNR', 'BS_PCODE',
              'BS_PLAATS', 'BS_RELSRTC', 'BS_STRAAT', 'BS_TOEVOEG', 'BS_VOORL', 'BS_VOORV',
            ]);
            for (const key of Object.keys(pp.BS)) {
              if (!allowedBS.has(key)) delete pp.BS[key];
            }
            // Ensure required BS fields have defaults
            if (!pp.BS.BS_ANAAM) pp.BS.BS_ANAAM = '';
            if (!pp.BS.BS_GESLACH) pp.BS.BS_GESLACH = 'O';
            if (!pp.BS.BS_VOORL) pp.BS.BS_VOORL = '';
            // Fill BS address from VP if missing
            const vpAddr = pp.VP || {};
            if (!pp.BS.BS_HUISNR && vpAddr.VP_HUISNR) pp.BS.BS_HUISNR = vpAddr.VP_HUISNR;
            if (!pp.BS.BS_PCODE && vpAddr.VP_PCODE) pp.BS.BS_PCODE = vpAddr.VP_PCODE;
            if (!pp.BS.BS_PLAATS && vpAddr.VP_PLAATS) pp.BS.BS_PLAATS = vpAddr.VP_PLAATS;
            if (!pp.BS.BS_STRAAT && vpAddr.VP_STRAAT) pp.BS.BS_STRAAT = vpAddr.VP_STRAAT;
          }

          // OB: whitelist (single source: personenauto/fields.ts). OB_EERSTEI is the
          // first-owner flag — must survive the request submission so Allianz can validate
          // Nieuwwaarderegeling (CG 2040) / Aanschafwaarderegeling (CG 2041) on the final
          // binding call.
          for (const key of Object.keys(pp.OB)) {
            if (!AUTO_DOWNSTREAM_OB_ALLOWLIST.has(key)) delete pp.OB[key];
          }
          // OB_AFMKM: the engine rates real kilometers — the wire carries the band km
          // (personenauto/fields.ts AFMKM table). The dropdown code rides along in
          // EXPERIMENT(OB_AFMKMCD) — self-invented label, safe to remove, see fields.ts.
          if (pp.OB.OB_AFMKM !== undefined) {
            const afmkm = afmkmCode(pp.OB.OB_AFMKMCD ?? pp.OB.OB_AFMKM);
            if (afmkm) pp.OB.OB_AFMKMCD = afmkm;
            pp.OB.OB_AFMKM = afmkmWireKm(pp.OB.OB_AFMKM);
          }
          if (pp.OB.OB_DAGWRDE !== undefined) pp.OB.OB_DAGWRDE = parseInt(String(pp.OB.OB_DAGWRDE), 10) || 0;
          // Ensure required OB fields have defaults
          if (!pp.OB.OB_GEBRUIK) pp.OB.OB_GEBRUIK = 'P';
          if (!pp.OB.OB_MLDCODE) pp.OB.OB_MLDCODE = '';
          if (!pp.OB.OB_TNOKLAS) pp.OB.OB_TNOKLAS = '';
          if (!pp.OB.OB_TYPE) pp.OB.OB_TYPE = pp.OB.OB_MODEL || '';

          // VE: whitelist
          if (pp.VE) {
            const allowedVE = new Set(['VE_MYAAND']);
            for (const key of Object.keys(pp.VE)) {
              if (!allowedVE.has(key)) delete pp.VE[key];
            }
          }

          // VP: whitelist + ensure required fields
          if (!pp.VP) pp.VP = {};
          const allowedVP = new Set(['VP_GEBDAT', 'VP_HUISNR', 'VP_PCODE', 'VP_PLAATS', 'VP_STRAAT', 'VP_TOEVOEG']);
          for (const key of Object.keys(pp.VP)) {
            if (!allowedVP.has(key)) delete pp.VP[key];
          }
          // Fill VP from BS if missing
          if (!pp.VP.VP_GEBDAT && pp.BS?.BS_GEBDAT) pp.VP.VP_GEBDAT = pp.BS.BS_GEBDAT;
          if (!pp.VP.VP_HUISNR && pp.BS?.BS_HUISNR) pp.VP.VP_HUISNR = pp.BS.BS_HUISNR;
          if (!pp.VP.VP_PCODE && pp.BS?.BS_PCODE) pp.VP.VP_PCODE = pp.BS.BS_PCODE;

          // RI: whitelist per auto.json schema
          if (pp.RI) {
            const allowedRI = new Set(['RI_ALGVRWD', 'RI_BMEXTRA', 'RI_SCH5JR', 'RI_EGD', 'RI_OGB', 'RI_POLJUS', 'RI_OVRMED', 'RI_CGGDA', 'RI_STVOOP']);
            for (const key of Object.keys(pp.RI)) {
              if (!allowedRI.has(key)) delete pp.RI[key];
            }
          }

          // WA: whitelist + ensure MP
          if (pp.WA) {
            if (!pp.WA.MP) pp.WA.MP = [];
            if (!pp.WA.WA_BMTABCD) pp.WA.WA_BMTABCD = 'PN23';
            if (!pp.WA.WA_WPREMBP) pp.WA.WA_WPREMBP = '4';
            const allowedWA = new Set([
              'MP', 'WA_BMCOMTR', 'WA_BMEXTRA', 'WA_BMNAAND', 'WA_BMPERC', 'WA_BMTABCD', 'WA_BTP',
              'WA_CODE', 'WA_ENTREF', 'WA_NCAAND', 'WA_NCPRC', 'WA_NTP', 'WA_PKRTVMP',
              'WA_PPRC', 'WA_PTSLTB', 'WA_SCHDVRY', 'WA_TASS', 'WA_TKRT', 'WA_TKRTBM',
              'WA_TPP', 'WA_TTOT', 'WA_TTPPRV', 'WA_TTSL', 'WA_TTSLBM', 'WA_TTSLTB',
              'WA_VZBLETS', 'WA_VZBMATS', 'WA_WPREMBP',
            ]);
            for (const key of Object.keys(pp.WA)) {
              if (!allowedWA.has(key)) delete pp.WA[key];
            }
          }

          // Default *_WPREMBP='4' across all PA coverage sections (schema-required per coverage).
          if (Array.isArray(pp.CG)) {
            pp.CG.forEach((cg: any) => { if (!cg.CG_WPREMBP) cg.CG_WPREMBP = '4'; });
          }
          if (Array.isArray(pp.CA)) {
            pp.CA.forEach((ca: any) => { if (!ca.CA_WPREMBP) ca.CA_WPREMBP = '4'; });
          }
          if (pp.RP && !pp.RP.RP_WPREMBP) pp.RP.RP_WPREMBP = '4';
          if (pp.SV && !pp.SV.SV_WPREMBP) pp.SV.SV_WPREMBP = '4';
          if (pp.PH && !pp.PH.PH_WPREMBP) pp.PH.PH_WPREMBP = '4';

          // VW: whitelist each item
          if (Array.isArray(pp.VW)) {
            const allowedVW = new Set(['VW_VOLGNUM', 'VW_VRWNR', 'VW_VRWOM', 'VW_VRWVERS', 'VW_URL']);
            pp.VW = pp.VW.map((item: any) => {
              const cleaned: any = {};
              for (const key of Object.keys(item)) {
                if (allowedVW.has(key)) cleaned[key] = item[key];
              }
              return cleaned;
            });
          } else {
            pp.VW = [];
          }

          // Required sections with defaults
          if (!pp.CL || !Array.isArray(pp.CL)) pp.CL = [];
          if (!pp.TP || typeof pp.TP !== 'object') pp.TP = {};
          if (!pp.VL || typeof pp.VL !== 'object') pp.VL = {};
          for (const key of Object.keys(pp.TP)) delete pp.TP[key];
          for (const key of Object.keys(pp.VL)) delete pp.VL[key];
          if (!pp.LC) pp.LC = { LC_NUMMER: '' };
          if (!pp.LI) pp.LI = {};
          for (const k of LI_KENTEKENHOUDER_PERSONAL) delete pp.LI[k];
          if (!pp.LP) pp.LP = { LP_ANAAM: '' };
          // CA, CG, PH, RP, SV: ensure present only when coverage was selected
          if (!pp.CA) pp.CA = [];
          if (!pp.CG) pp.CG = [];
          if (!pp.PH) pp.PH = {};
          // RP and SV: only include when user selected the coverage (has premium data)
          // If only _CODE and _PPRC are present (no premium), the coverage was not selected
          const autoDekkingen = getStepData('personenauto')?.Supportsection?.Dekkingen || {};
          if (autoDekkingen.Rechtsbijstand === 'J') {
            if (!pp.RP) pp.RP = { RP_CODE: '9110', RP_PPRC: 20 };
          } else {
            delete pp.RP;
          }
          if (autoDekkingen.Schade_Inzittenden === 'J') {
            if (!pp.SV) pp.SV = { SV_CODE: '1002', SV_PPRC: 20 };
          } else {
            delete pp.SV;
          }

          // PP_PVADAT must be string
          if (pp.PP_PVADAT !== undefined && typeof pp.PP_PVADAT !== 'string') {
            pp.PP_PVADAT = String(pp.PP_PVADAT);
          }
          // PP_STATUS must be string
          if (pp.PP_STATUS !== undefined && typeof pp.PP_STATUS !== 'string') {
            pp.PP_STATUS = String(pp.PP_STATUS);
          }

          // Ensure required PP scalar fields
          if (!pp.PP_HANDACC) pp.PP_HANDACC = 'N';
          if (!pp.PP_INCWIJZ) pp.PP_INCWIJZ = 'I';
          // PP_EKST - one-time poliskosten (cap-aware allocation across the package; personenauto branch)
          pp.PP_EKST = policyAllocation.personenauto;
          if (pp.PP_SPLIMNR === undefined) pp.PP_SPLIMNR = '';
          if (pp.PP_BMEXTRA === undefined) pp.PP_BMEXTRA = 0;
          delete pp.PP_SCHDVRM;

          // Strip BA (UI-only address data)
          delete pp.BA;

          // PP whitelist: only schema-allowed fields
          const allowedPP = new Set([
            'AA', 'BS', 'CA', 'CG', 'CL', 'EL', 'LC', 'LI', 'LP', 'OB', 'PH', 'QS', 'RI', 'RP', 'SV',
            'TP', 'VE', 'VL', 'VP', 'VW', 'WA',
            'PP_AFDDEFN', 'PP_AFWAFSP', 'PP_AFWAFST', 'PP_AGNTNR', 'PP_BETTERM', 'PP_BMCOMTR',
            'PP_BMEXTRA', 'PP_BMINSCH', 'PP_BMNAAND', 'PP_BMPERC', 'PP_BRANCHE', 'PP_CDUUMND',
            'PP_COLL', 'PP_EKST', 'PP_EXPDAT', 'PP_EXTERN', 'PP_HANDACC',
            'PP_HVVDAT', 'PP_IBAN', 'PP_INCWIJZ', 'PP_INGDAT', 'PP_INGDTW', 'PP_IVBLETC',
            'PP_MYAAND', 'PP_MYNAAM', 'PP_NCAAND', 'PP_NCPRC', 'PP_NTP', 'PP_PKRTVMP',
            'PP_PKST', 'PP_PPRC', 'PP_PRODNM', 'PP_PTSLTB', 'PP_PVADAT', 'PP_SCHDVRY', 'PP_SLOTAKK',
            'PP_SPECLIM', 'PP_SPLIMNR', 'PP_STATUS', 'PP_TARDAT', 'PP_TASS',
            'PP_TKRTBM', 'PP_TPKRTVM', 'PP_TPP', 'PP_TTOT', 'PP_TTPPRV',
            'PP_TTSLBM', 'PP_TTSLTB', 'PP_WPREMBP',
          ]);
          for (const key of Object.keys(pp)) {
            if (!allowedPP.has(key)) delete pp[key];
          }

        }
      });


      const allQuotes = requestDocument.Pakket?.[0]?.Onderdeel || requestDocument.Contract || [];
      for (const q of allQuotes) {
        applyCaDerivedFields(q.PP);
      }

      // Stamp coverage order (XX_VOLGNUM: WA first, then CA, then extras) on the personenauto
      // contract in the final request (handles both Pakket and flat Contract shapes).
      orderSaaCoveragesInDocument(requestDocument);

      // DEBUG: Log request (only in dev/local)
      if (import.meta.env.DEV) {
        console.log('[Confirmation] Request:', requestDocument);
      }

      // Call request endpoint with calculation ID
      const response = await createRequest(relationId, quoteId, requestDocument, calculationId || undefined);

      // DEBUG: Log response + persist request/response so the EndPage can show them (dev only)
      if (import.meta.env.DEV) {
        console.log('[Confirmation] Response:', response);
        try {
          sessionStorage.setItem('debug_create_request', JSON.stringify(requestDocument));
          sessionStorage.setItem('debug_create_response', JSON.stringify(response));
        } catch { /* ignore quota/serialise errors */ }
      }

      // Store request ID if returned (check multiple possible field names)
      let requestId = null;
      if (response) {
        requestId = response.requestId || response.RequestId || response.request_id ||
                    response.data?.requestId || response.data?.RequestId || response.data?.request_id ||
                    null;

        if (requestId) {
          sessionStorage.setItem('request_id', requestId);
          // console.log('[Confirmation] Stored request ID:', requestId);
        } else {
          // console.warn('[Confirmation] No request ID found in response, checked fields:', {
          //   requestId: response.requestId,
          //   RequestId: response.RequestId,
          //   request_id: response.request_id,
          //   data: response.data
          // });
        }
      }

      // Set flag that request was successfully submitted (for email sending)
      // Use relation_id + quote_id as unique identifier since we know these exist
      const emailKey = requestSubmittedKey(relationId, quoteId);
      sessionStorage.setItem(emailKey, 'true');
      setRequestInFlight(false);
      // console.log('[Confirmation] Request submitted, email key set:', emailKey);

      // Navigate to end page only after successful request
      navigate('/workflow/end');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit application. Please try again.';
      setSubmitError(errorMessage);
      setIsSubmitting(false);
      setRequestInFlight(false);

      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    navigate('/workflow/acceptance-questions');
  };

  const selectedProductCount = [
    selectedProducts?.aansprakelijkheid,
    selectedProducts?.inboedel,
    selectedProducts?.woonhuis,
    selectedProducts?.personenauto,
  ].filter(Boolean).length;

  const subscriptionFeeConfig = (config as any)?.subscriptionFee;
  const subscriptionFee = subscriptionFeeConfig?.enabled
    ? (selectedProductCount > 2 ? (subscriptionFeeConfig.tier3 || 20.00) : (subscriptionFeeConfig.tier1 || 12.50))
    : 0;
  const prolongationFeeConfig = (config as any)?.prolongationFee;
  const prolongationFee = prolongationFeeConfig?.enabled ? (prolongationFeeConfig.amount || 2.50) : 0;

  // Get individual product premium (as a number). AVP / Inboedel / Woonhuis always read workflow
  // data so the per-product line shows only the product discount (not the BFF's package discount
  // layered on top). Personenauto / Rechtsbijstand fall back to packageProduct.PP_TTOT when the
  // enriched doc is available.
  const getProductPremiumNumber = (productType: 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand'): number => {
    if (productType === 'aansprakelijkheid') {
      const avp = computeAvpDisplayPremium(data?.['aansprakelijkheid'], config as any);
      return avp > 0 ? avp : 0;
    }
    if (productType === 'inboedel') {
      // Use the shared per-section helper so this matches the Inboedel screen's badge sum exactly.
      const inb = computeInboedelTotalFromSections(data?.['inboedel'], config as any);
      return inb > 0 ? inb : 0;
    }
    if (productType === 'woonhuis') {
      const wh = computeWoonhuisDisplayPremium(data?.['woonhuis'], config as any);
      return wh > 0 ? wh : 0;
    }
    try {
      const enrichedDoc = sessionStorage.getItem('enriched_calculation_document');
      if (enrichedDoc) {
        const doc = JSON.parse(enrichedDoc);
        const packageProducts = doc?.Pakket?.[0]?.Onderdeel || doc?.Contract || [];
        const pkg = productType === 'personenauto'
          ? packageProducts.find((p: any) => p.PP?.OB)
          : packageProducts.find((p: any) => p.PP?.WC && p.PP?.PP_EXTERN === 'RB');
        if (pkg?.PP?.PP_TTOT) return Number(pkg.PP.PP_TTOT) || 0;
      }
    } catch (error) { /* fall back */ }
    const premium = data?.[productType]?.Contractdocument?.PP?.PP_TTOT;
    return premium ? parseFloat(premium) : 0;
  };

  const getProductPremium = (productType: 'aansprakelijkheid' | 'inboedel' | 'woonhuis' | 'personenauto' | 'rechtsbijstand'): string => {
    const n = getProductPremiumNumber(productType);
    return n > 0 ? formatPrice(n) : '0,00';
  };

  // Calculate total monthly premium by summing per-product ex-provision helper values (same as PaymentInformationScreen).
  // PK_TTOT is not used here because it includes the provision, which would mismatch the per-product ex-provision display.
  const calculateTotalMonthly = () => {
    const productTypes: Array<'woonhuis' | 'inboedel' | 'aansprakelijkheid' | 'personenauto' | 'rechtsbijstand'> = [
      'woonhuis', 'inboedel', 'aansprakelijkheid', 'personenauto', 'rechtsbijstand',
    ];
    let total = 0;
    for (const type of productTypes) {
      if (selectedProducts?.[type]) total += getProductPremiumNumber(type);
    }
    return formatPrice(total + subscriptionFee + prolongationFee);
  };

  const currentStep = getCurrentApplicationStepNumber('/workflow/confirmation');
  const totalSteps = calculateApplicationSteps();

  if (alreadySubmitted) return null;

  // Collected current-page data for the shared debug panel.
  const debugCollected: Record<string, unknown> = {
    ids: readDebugIds(),
    relation: data?.['client-information']?.clientInfo ?? null,
    payment: getStepData('payment-information-request') ?? null,
    premium: buildPremiumDebugSection(selectedProducts, data, config),
    package: readEnrichedDocument(),
    products: collectProducts(selectedProducts, data),
  };

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps} flowType="application">
      <main style={styles.page}>
        <div style={styles.backgroundDecoration}></div>
        <div style={styles.container}>
          <header style={styles.header}>
            <h1 style={styles.title}>{t('screens.paymentInfo.title')}</h1>
            <p style={styles.subtitle}>{t('screens.paymentInfo.subtitle')}</p>
          </header>

          {/* Error Banner */}
          {submitError && (
            <div style={styles.errorBanner}>
              <span>✗ {submitError}</span>
            </div>
          )}

          <div style={styles.contentWrapper} className="confirmation-content-wrapper">
            {/* Main Content */}
            <div style={styles.mainContent} className="confirmation-main-content">
              {/* Application Summary */}
              <div style={styles.card}>
                <div style={styles.sectionHeader}>
                  <svg style={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 11l3 3L22 4"></path>
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
                  </svg>
                  <h2 style={styles.sectionTitle}>{t('screens.confirmation.applicationSummaryTitle')}</h2>
                </div>

                {/* Shared applicant presentation (single source with the Offerte gegevens card):
                    personal grid + deduplicated Verzekerd-object/voertuig/personen boxes. */}
                <ApplicantSummary />

                {/* Payment & Start Date */}
                <div style={styles.infoSectionLast}>
                  <h3 style={styles.infoSectionTitle}>{t('screens.confirmation.paymentDetails')}</h3>
                  <div className="summary-info-grid" style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>{t('screens.confirmation.startDate')}</span>
                      <span style={styles.infoValue}>{paymentData?.paymentInfo?.startDate || '-'}</span>
                    </div>
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>{t('screens.confirmation.iban')}</span>
                      <span style={styles.infoValue}>{paymentData?.paymentInfo?.iban || '-'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Slotverklaring, Privacyverklaring & SEPA Toestemming — stacked under
                  Persoonsgegevens in the left column; the sticky sidebar tracks them. */}
              <div>
                {/* Slotverklaring */}
                <div style={styles.card}>
                  <div style={styles.sectionHeader}>
                    <svg style={styles.sectionIconSuccess} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <h2 style={styles.sectionTitle}>{t('screens.confirmation.termsTitle')}</h2>
                  </div>

                  <p style={styles.statementText}>
                    {t('screens.confirmation.declarationIntro')}
                  </p>
                  <ul style={styles.statementList}>
                    <li>{t('screens.confirmation.declaration1')}</li>
                    <li>{t('screens.confirmation.declaration2')}</li>
                    <li>{t('screens.confirmation.declaration3')}</li>
                    <li>{t('screens.confirmation.declaration4')}</li>
                    <li>{t('screens.confirmation.declaration5')}</li>
                    <li>{t('screens.confirmation.declaration6')}</li>
                    <li>{t('screens.confirmation.declaration7')}</li>
                    <li>{t('screens.confirmation.declaration8')}</li>
                    <li>{t('screens.confirmation.declaration9')}</li>
                    <li>{t('screens.confirmation.declaration10')}</li>
                    <li>{t('screens.confirmation.declaration11')}</li>
                    <li>{t('screens.confirmation.declaration12')}</li>
                  </ul>

                  {/* Slotverklaring checkbox */}
                  <div style={styles.checkboxContainer}>
                    <input
                      id="accept-terms"
                      type="checkbox"
                      checked={paymentData?.paymentInfo?.acceptTerms || false}
                      onChange={(e) => {
                        handleCheckboxChange(e.target.checked);
                        if (e.target.checked) setTermsError(false);
                      }}
                      style={{
                        ...styles.checkbox,
                        ...(paymentData?.paymentInfo?.acceptTerms ? styles.checkboxChecked : {})
                      }}
                      tabIndex={0}
                      required
                    />
                    <div style={styles.checkboxLabelContainer}>
                      <label style={styles.checkboxLabel} htmlFor="accept-terms">
                        {t('screens.confirmation.termsCheckbox')} <span style={styles.required}>*</span>
                      </label>
                      {termsError && (
                        <div style={styles.errorMessage}>
                          <svg style={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                          {t('screens.confirmation.termsError')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Privacyverklaring */}
                <div style={styles.card}>
                  <div style={styles.sectionHeader}>
                    <svg style={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                    </svg>
                    <h2 style={styles.sectionTitle}>{t('screens.confirmation.privacyTitle')}</h2>
                  </div>
                  <div style={styles.checkboxContainer}>
                    <input
                      id="accept-privacy"
                      type="checkbox"
                      checked={privacyAccepted}
                      onChange={(e) => {
                        setPrivacyAccepted(e.target.checked);
                        if (e.target.checked) setPrivacyError(false);
                        setPaymentData({
                          ...paymentData,
                          paymentInfo: {
                            ...paymentData?.paymentInfo,
                            privacyAccepted: e.target.checked,
                          },
                        });
                      }}
                      style={{
                        ...styles.checkbox,
                        ...(privacyAccepted ? styles.checkboxChecked : {})
                      }}
                      tabIndex={0}
                      required
                    />
                    <div style={styles.checkboxLabelContainer}>
                      <label style={styles.checkboxLabel} htmlFor="accept-privacy">
                        {t('screens.confirmation.privacyCheckbox')} <span style={styles.required}>*</span>
                      </label>
                      <p style={styles.checkboxHelpText}>
                        {t('screens.confirmation.privacyDescription')}{' '}
                        <a
                          href={config?.branding?.privacyUrl || DEFAULT_PRIVACY_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.inlineLink}
                        >
                          {t('screens.confirmation.privacyLink')}
                        </a>
                      </p>
                      {privacyError && (
                        <div style={styles.errorMessage}>
                          <svg style={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                          {t('screens.confirmation.privacyError')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* SEPA Toestemming */}
                <div style={styles.card}>
                  <div style={styles.sectionHeader}>
                    <svg style={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                    <h2 style={styles.sectionTitle}>{t('screens.confirmation.sepaTitle')}</h2>
                  </div>
                  <div style={styles.checkboxContainer}>
                    <input
                      id="accept-sepa"
                      type="checkbox"
                      checked={sepaAccepted}
                      onChange={(e) => {
                        setSepaAccepted(e.target.checked);
                        if (e.target.checked) setSepaError(false);
                        setPaymentData({
                          ...paymentData,
                          paymentInfo: {
                            ...paymentData?.paymentInfo,
                            sepaAccepted: e.target.checked,
                          },
                        });
                      }}
                      style={{
                        ...styles.checkbox,
                        ...(sepaAccepted ? styles.checkboxChecked : {})
                      }}
                      tabIndex={0}
                      required
                    />
                    <div style={styles.checkboxLabelContainer}>
                      <label style={styles.checkboxLabel} htmlFor="accept-sepa">
                        {t('screens.confirmation.sepaCheckbox')} <span style={styles.required}>*</span>
                      </label>
                      <p style={styles.checkboxHelpText}>
                        {t('screens.confirmation.sepaDescription', {
                          // Creditor in the SEPA mandate is always the SAA entity, not the per-brand
                          // appName (NU91/DFD/SPD) — emailDisplayName is "SAA Verzekeringen B.V." for
                          // every brand, matching the footer/email. Applies to NL and EN (interpolated).
                          companyName: (config?.branding as any)?.emailDisplayName || 'SAA Verzekeringen',
                          agentName: config?.customer?.name || 'DFD De Financiele Dienstverleners B.V.',
                        })}
                      </p>
                      {sepaError && (
                        <div style={styles.errorMessage}>
                          <svg style={styles.errorIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                          </svg>
                          {t('screens.confirmation.sepaError')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>


            <WorkflowNavButtons
              className="confirmation-footer"
              onBack={handleBack}
              onNext={handleConfirm}
              backDisabled={isSubmitting}
              nextDisabled={isSubmitting}
              nextLoading={isSubmitting}
              nextLabel={isSubmitting ? 'screens.confirmation.submitting' : 'screens.confirmation.submit'}
            />
            </div>

            {/* Sidebar */}
            <aside style={styles.sidebar} className="confirmation-sidebar" tabIndex={-1}>
              {/* Summary Card — toggle: USE_BREAKDOWN_UI=true shows shared breakdown card, false shows classic product list. */}
              <div style={styles.summaryCard} className="confirmation-summary-card" tabIndex={-1}>
                <div style={styles.sidebarHeader}>
                  <svg style={styles.sidebarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" tabIndex={-1} aria-hidden="true">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <h3 style={styles.sidebarTitle}>{t('screens.paymentInfo.sidebar.summary')}</h3>
                </div>
                {USE_BREAKDOWN_UI ? (
                  <PremiumBreakdownCard skipChainGate />
                ) : (
                  <>
                    {/* Product list order: Personenauto → Woonhuis → Inboedel → AVP → Rechtsbijstand */}
                    <div style={styles.productList}>
                      {selectedProducts?.personenauto && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17h14M5 17a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1l2-3h8l2 3h1a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2"/><circle cx="7.5" cy="17" r="2"/><circle cx="16.5" cy="17" r="2"/></svg>
                            <span style={styles.productName}>{t(getProductContent('personenauto').displayNameKey)}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('personenauto')}</span>
                        </div>
                      )}
                      {selectedProducts?.woonhuis && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            <span style={styles.productName}>{t(getProductContent('woonhuis').displayNameKey)}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('woonhuis')}</span>
                        </div>
                      )}
                      {selectedProducts?.inboedel && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 9v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9"/><path d="M9 22V12h6v10"/><path d="M2 10.6L12 2l10 8.6"/></svg>
                            <span style={styles.productName}>{t(getProductContent('inboedel').displayNameKey)}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('inboedel')}</span>
                        </div>
                      )}
                      {selectedProducts?.aansprakelijkheid && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                            <span style={styles.productName}>{t(getProductContent('aansprakelijkheid').displayNameKey)}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('aansprakelijkheid')}</span>
                        </div>
                      )}
                      {selectedProducts?.rechtsbijstand && (
                        <div style={styles.productItem}>
                          <div style={styles.productItemLabel}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color, #056666)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v18M2 7l10-4 10 4M2 7l4 9h4M14 16h4l4-9"/><circle cx="6" cy="16" r="2"/><circle cx="18" cy="16" r="2"/></svg>
                            <span style={styles.productName}>{t(getProductContent('rechtsbijstand').displayNameKey)}</span>
                          </div>
                          <span style={styles.productPrice}>€{getProductPremium('rechtsbijstand')}</span>
                        </div>
                      )}
                    </div>
                    <div style={styles.productListDivider}></div>
                    <div style={styles.feeList}>
                      <div style={styles.feeRow}>
                        <span style={styles.feeLabel}>{t('subscription.label')}</span>
                        <span style={styles.feeLabel}>€{formatPrice(subscriptionFee)}</span>
                      </div>
                      <div style={styles.feeRow}>
                        <span style={styles.feeLabel}>{t('prolongation.label')}</span>
                        <span style={styles.feeLabel}>€{formatPrice(prolongationFee)}</span>
                      </div>
                    </div>
                    <div style={styles.divider}></div>
                    <div style={styles.totalRow}>
                      <span style={styles.totalLabel}>{t('screens.paymentInfo.sidebar.totalMonthly')}</span>
                      <span style={styles.totalAmount}>€{calculateTotalMonthly()}</span>
                    </div>
                    <div style={styles.taxNote}>{t('screens.offerSummary.includingInsuranceTax')}</div>
                  </>
                )}
              </div>

              {/* Help Card */}
              <HelpCard className="confirmation-help-card" />
              <SubscriptionCard className="confirmation-benefits-card" />
            </aside>
          </div>

          {isDebugMode() && <WorkflowDebugPanel collected={debugCollected} />}

        </div>
      </main>
    </WorkflowPageLayout>
  );
};

export default ConfirmationScreen;
